<template>
  <div
    class="l-node"
    :class="{
      'l-node--readonly': readonly,
      'l-node--carbon': isCarbonNode,
    }"
  >
    <div class="l-node-title">
      <template v-if="isCloneChild">
        <span>{{ cloneTitleLabel }}</span>
      </template>
      <template v-else>
        {{ n.option && n.option.name || (n.graph && n.graph.text && n.graph.text.value) }}
        <span v-if="multiple" style="margin-left:3px">(或签)</span>
        <span v-if="n.option && n.option.clonedBy" style="margin-left:3px">
          (关联:{{ n.option.clonedType }}[{{ clonedByLabel }}])
        </span>
      </template>
    </div>
    <div class="l-node-content">
      <template v-if="n.option && n.option.clonedBy">
        <template v-if="clonedNodes && clonedNodes.length">
          <FlowNodeCreate
            v-for="cn in clonedNodes"
            :key="cn.id"
            :value="getClonedSlot(cn.id)"
            :db="db"
            :node="cn"
            :name-control="clonedType"
            :readonly="readonly"
            @input="setClonedSlot(cn.id, $event)"
          />
        </template>
        <template v-else-if="formDbPending">
          <BaseSkeletonLoading variant="list" :count="2" />
        </template>
        <template v-else>
          <div class="clone-wait-tip">
            请先在表单填写「{{ clonedByLabel }}」
          </div>
        </template>
      </template>
      <template v-else>
        <div class="flex-wrap">
          <div style="width:50px;flex-shrink:0;color:var(--primary);text-align:center;">{{ whoLabel }}</div>
          <BaseUserSelectGrouped
            style="width:200px;border-radius:6px;border:1px solid #336699aa;"
            :value="localValue.executors"
            :editable="!readonly && executorType !== '固定'"
            @input="setExecutors"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import {mapGetters} from 'vuex';
import {resolveCloneItemMeta, resolveClonedDefaultUsers} from '@/components/flow/clone-display';

export default {
  name: 'FlowNodeCreate',
  props: {
    value: {},
    db: {},
    node: {},
    'name-control': {},
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      syncing: false,
    };
  },
  watch: {
    value: {
      handler(v) {
        if (this.readonly) return;
        if (v == null) {
          this.initNodeData();
          return;
        }
        // 克隆子项：槽位已存在但未选人时，用节点上的分类默认人补齐
        if (this.isCloneChild) {
          this.ensureChildDefaultExecutors(v);
        }
      },
      immediate: true,
    },
    /** 只关心克隆源字段的 key 集合变化，不 deep watch 整表 db（避免输入时刷屏） */
    clonedBySignature: {
      handler() {
        this.syncClonedSlots();
      },
      immediate: true,
    },
    clonedByKey() {
      this.syncClonedSlots();
    },
  },
  computed: {
    ...mapGetters('session', ['deps', 'users']),
    n() {
      return this.node || {};
    },
    isCloneChild() {
      return !!(this.n._cloneKey || (this.n.option && this.n.option.cloneValue != null));
    },
    cloneTitleLabel() {
      return this.n._cloneLabel || (this.n.option && this.n.option.name) || this.n._cloneKey || '';
    },
    formDbPending() {
      const d = this.d;
      if (!d || typeof d !== 'object') return true;
      return Object.keys(d).length === 0;
    },
    option() {
      return this.n.option || {};
    },
    multiple() {
      return this.option.multiple;
    },
    executorType() {
      return this.option['executor-type'];
    },
    whoLabel() {
      const t = this.n.graph && this.n.graph.type;
      return t === 'carbon_node' ? '抄送人' : '负责人';
    },
    isCarbonNode() {
      return (this.n.graph && this.n.graph.type) === 'carbon_node';
    },
    defaultUsers() {
      if (this.executorType === '固定' && this.n.graph && this.n.graph.type === 'start_node') {
        return this.$store.getters['session/session'].id;
      }
      return this.option['default-users'];
    },
    region() {
      return this.option['approval-region'];
    },
    d() {
      return this.db || {};
    },
    localValue() {
      return this.value || {};
    },
    clonedType() {
      const clonedTypeMap = {
        '部门': 'BaseDepBoard',
        '员工': 'BaseNameBoard',
        '成员': 'BaseNameBoard',
      };
      return clonedTypeMap[this.n.option && this.n.option.clonedType];
    },
    clonedByKey() {
      const clonedBy = this.n.option && this.n.option.clonedBy;
      if (!clonedBy) return '';
      if (typeof clonedBy === 'string') return clonedBy;
      return clonedBy.key || '';
    },
    clonedByLabel() {
      const clonedBy = this.n.option && this.n.option.clonedBy;
      if (!clonedBy) return (this.n.option && this.n.option.cloneBy) || '';
      if (typeof clonedBy === 'string') return clonedBy;
      return (clonedBy.control && clonedBy.control.name) || clonedBy.key || '';
    },
    clonedBy() {
      const path = this.clonedByKey;
      if (!path) return null;
      const paths = path.split('.').filter(Boolean);
      let relatedValues = this.d;
      for (let i = 0; i < paths.length; i++) {
        if (relatedValues == null) return null;
        relatedValues = relatedValues[paths[i]];
      }
      return relatedValues;
    },
    clonedByList() {
      const raw = this.clonedBy;
      if (raw == null || raw === '') return [];
      if (Array.isArray(raw)) return raw.filter((v) => v != null && v !== '');
      return [raw];
    },
    /** 克隆对象身份签名：仅 key 变化才触发同步 */
    clonedBySignature() {
      if (!this.clonedByKey) return '';
      const clonedBy = this.n.option && this.n.option.clonedBy;
      const ctx = this.cloneResolveCtx;
      return this.clonedByList.map((item) => {
        const meta = resolveCloneItemMeta(item, clonedBy, ctx);
        return meta.keyPart;
      }).join('|');
    },
    cloneResolveCtx() {
      return {
        deps: this.deps || [],
        users: this.users || [],
        clonedType: this.n.option && this.n.option.clonedType,
      };
    },
    clonedDefaultUsersMap() {
      return (this.n.option && this.n.option.clonedDefaultUsers) || {};
    },
    clonedNodes() {
      const list = this.clonedByList;
      if (!list.length) return null;
      const clonedBy = this.n.option && this.n.option.clonedBy;
      const map = this.clonedDefaultUsersMap;
      const fallback = this.option['default-users'];
      return list.map((v) => {
        const meta = resolveCloneItemMeta(v, clonedBy, this.cloneResolveCtx);
        const keyPart = meta.keyPart;
        const namePart = meta.namePart;
        const raw = meta.raw;
        const cn = cloneDeep(this.n);
        delete cn.option.clonedBy;
        delete cn.option.cloneKey;
        delete cn.option.clonedKey;
        delete cn.option.clonedDefaultUsers;
        cn.id = this.n.id + '_' + keyPart;
        const defaults = resolveClonedDefaultUsers(map, keyPart, fallback);
        cn.option = Object.assign({}, cn.option || {}, {
          name: namePart,
          cloneValue: raw,
          'default-users': defaults,
        });
        cn._cloneKey = keyPart;
        cn._cloneLabel = namePart;
        return cn;
      });
    },
  },
  methods: {
    slotHasExecutors(slot) {
      if (!slot) return false;
      if (Array.isArray(slot.executors)) return slot.executors.length > 0;
      return !!slot.executors;
    },
    formatExecutors(defaults) {
      if (!defaults || !defaults.length) return null;
      if (this.multiple) return [...defaults];
      return defaults.length === 1 ? defaults[0] : [...defaults];
    },
    defaultsForCloneKey(keyPart) {
      return resolveClonedDefaultUsers(
        this.clonedDefaultUsersMap,
        keyPart,
        this.option['default-users'],
      );
    },
    ensureChildDefaultExecutors(v) {
      if (this.readonly || this.syncing) return;
      if (this.slotHasExecutors(v)) return;
      let ids = [];
      if (Array.isArray(this.defaultUsers)) {
        ids = this.defaultUsers.map(String).filter(Boolean);
      } else if (this.defaultUsers != null && this.defaultUsers !== '') {
        ids = [String(this.defaultUsers)];
      }
      if (!ids.length) return;
      const executors = this.formatExecutors(ids);
      this.syncing = true;
      this.$emit('input', Object.assign({}, v || {}, {executors}));
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
    initNodeData() {
      const defaultValue = {};
      if (this.n.option && this.n.option.clonedBy) {
        defaultValue.cloned = {};
      }
      if (this.defaultUsers != null && this.defaultUsers !== '') {
        if (Array.isArray(this.defaultUsers)) {
          if (this.defaultUsers.length) {
            defaultValue.executors = this.formatExecutors(this.defaultUsers.map(String));
          }
        } else {
          defaultValue.executors = this.defaultUsers;
        }
      }
      this.$emit('input', defaultValue);
      this.$nextTick(() => this.syncClonedSlots());
      return defaultValue;
    },
    getClonedSlot(id) {
      const cloned = (this.value && this.value.cloned) || {};
      return cloned[id] != null ? cloned[id] : null;
    },
    setClonedSlot(id, val) {
      if (this.readonly) return;
      const base = Object.assign({}, this.value || {});
      const cloned = Object.assign({}, base.cloned || {});
      cloned[id] = val;
      base.cloned = cloned;
      this.$emit('input', base);
    },
    setExecutors(v) {
      if (this.readonly) return;
      this.$emit('input', Object.assign({}, this.value || {}, {executors: v}));
    },
    syncClonedSlots(nodes) {
      if (this.readonly || this.syncing) return;
      if (!(this.n.option && this.n.option.clonedBy)) return;

      const list = nodes !== undefined ? nodes : this.clonedNodes;
      const base = Object.assign({}, this.value || {});
      const prev = Object.assign({}, base.cloned || {});
      const next = {};
      let changed = false;

      if (Array.isArray(list) && list.length) {
        list.forEach((cn) => {
          const prevSlot = prev[cn.id];
          if (this.slotHasExecutors(prevSlot)) {
            next[cn.id] = prevSlot;
            return;
          }
          // 优先分类映射，其次子节点已写入的 default-users，再次全局 default-users
          let defaults = this.defaultsForCloneKey(cn._cloneKey);
          if (!defaults.length && cn.option && cn.option['default-users']) {
            defaults = resolveClonedDefaultUsers(null, null, cn.option['default-users']);
          }
          if (defaults.length) {
            next[cn.id] = Object.assign({}, prevSlot || {}, {
              executors: this.formatExecutors(defaults),
            });
            changed = true;
          } else {
            next[cn.id] = prevSlot != null ? prevSlot : {};
            if (prevSlot == null) changed = true;
          }
        });
      } else if (Object.keys(prev).length) {
        // 表单清空克隆源：清空槽位
        changed = true;
      }

      const prevKeys = Object.keys(prev).sort().join('|');
      const nextKeys = Object.keys(next).sort().join('|');
      if (prevKeys === nextKeys && base.cloned && !changed) {
        return;
      }

      base.cloned = next;
      this.syncing = true;
      this.$emit('input', base);
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
  },
};
</script>

<style lang="less" scoped>
.l-node{
    background:#33669933;
    border:1px solid #336699aa;
    border-radius:5px;
    overflow:visible;
    position: relative;
    margin-left:5px;
    .l-node-title{
        display: flex;
        align-items: center;
        padding:0 5px;
        height:30px;
        border-bottom: 1px solid #336699aa;
        color:var(--primary);
    }
    .l-node-content{
        padding:5px;
    }
    .clone-wait-tip{
        font-size:12px;
        color:var(--subtext2);
        padding:8px 6px;
        line-height:1.5;
    }
}

.l-node:before{
    content:'';
    border:6px solid transparent;
    border-color: transparent #336699aa transparent transparent ;
    position: absolute;
    left:-13px;
    top:4px;
}

.l-node{
    .l-node{
        background:none;
        border-color:#33669955;
        margin:0;
        margin-bottom:5px
    }
    .l-node-title{
        border-color:#33669955;
    }
    .l-node:before{
        display: none;
    }

    .l-node:last-child{
        margin-bottom:0;
    }
}
.l-node.l-node--carbon{
    border-style: dashed;
    margin-top: 6px;
    background: #33669918;
}
.l-node.l-node--readonly{
    filter: none;
}
</style>
